import { HostingType } from 'components/create-visit-form-v2/types/form-data.types';
import { z } from 'zod';
import type { VisitStepFieldsConfig } from '../../hooks/use-ui-metadata-field-config.hook';

/**
 * Schema for host step validation
 * @param config - Field configuration from ui_metadata
 * @param hasSelectableFloors - Whether the building exposes at least one selectable floor.
 *   When false, the `floor` field is treated as optional even if `config.floor.required` is true,
 *   because there is nothing for the user to choose from.
 * @returns Zod schema object
 */
export const createHostStepSchema = (config: VisitStepFieldsConfig, hasSelectableFloors = true) =>
  z
    .object({
      hostingType: z.enum(HostingType).refine((val) => val !== undefined, {
        message: 'errors.hostingTypeIsRequired',
        path: ['hostingType'],
      }),
      receiveInvitationCopy: z.boolean().optional(),
      receiveCheckinNotifications: z.boolean().optional(),
      additionalOrganizers: config.additionalRecipients.required
        ? z.string().min(1, 'errors.additionalOrganizersRequired')
        : z.string().optional(),
      floor:
        config.floor.required && hasSelectableFloors
          ? z.string().min(1, 'errors.floorIsRequired')
          : z.string().optional().or(z.literal('')),
      suite: z.string().optional(),
      hostEmail: z.string().optional(),
    })
    .refine(
      (data) => {
        if (data.additionalOrganizers) {
          const emailSchema = z.string().email();
          return data.additionalOrganizers.split(',').every((email) => {
            const trimmed = email.trim();
            return trimmed.length > 0 && emailSchema.safeParse(trimmed).success;
          });
        }
        return true;
      },
      {
        message: 'errors.additionalOrganizersInvalid',
        path: ['additionalOrganizers'],
      },
    )
    .refine(
      (data) => {
        // hostEmail is only required when hostingType is OTHER
        if (data.hostingType === HostingType.OTHER) {
          return data.hostEmail !== undefined && data.hostEmail !== null && data.hostEmail.trim().length > 0;
        }
        // hostEmail is optional when hostingType is SELF
        return true;
      },
      {
        message: 'errors.hostEmailIsRequired',
        path: ['hostEmail'],
      },
    );
